import { useCallback, useEffect, useMemo, useState } from "react";
import { api, errorText } from "./api";
import { Dialogs } from "./components/Dialogs";
import { ConsoleSkeleton } from "./components/Skeleton";
import { Sparkline } from "./components/Sparkline";
import { TimeAgo } from "./components/TimeAgo";
import { Toasts } from "./components/Toasts";
import { confirmDialog } from "./dialog";
import { toast } from "./toast";

/* The operator's console.

   Scope is narrow on purpose, and the API enforces it rather than this page
   being polite about it: mint codes, see who exists, suspend, delete, reissue a
   password. There is no "log in as this tenant" and no way to read their key or
   their history — running the platform is not the same as having a window into
   a client's account. */

interface Tenant {
  id: number;
  slug: string;
  display_name: string;
  status: string;
  created_at: number;
  last_seen_at: number | null;
  requests_total: number;
  requests_today: number;
  chars_today: number;
  /* Messages per day, oldest first, always the full window. */
  daily: number[];
}

interface Invite {
  code: string;
  note: string;
  created_at: number;
  expires_at: number | null;
  used_at: number | null;
  tenant_id: number | null;
}

interface AccessRequest {
  id: number;
  created_at: number;
  handle: string;
  contact: string;
  note: string;
  ip: string;
  status: string;
  decided_at: number | null;
  invite_code: string | null;
}

interface Stats {
  tenants: number;
  active: number;
  suspended: number;
  requests_today: number;
  chars_today: number;
  max_tenants: number;
  pending_requests: number;
}

/* The console is a monitoring surface, so it refreshes itself: a table you have
   to reload to trust is one an operator stops believing. */
const REFRESH_MS = 30_000;

export function Platform() {
  const [authed, setAuthed] = useState<boolean | null>(null);
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api("/api/platform/me")
      .then(() => setAuthed(true))
      .catch(() => setAuthed(false));
  }, []);

  if (authed === null) return <ConsoleSkeleton />;

  if (!authed) {
    return (
      <main className="platform login-shell">
        <h1>ToonSpeech admin</h1>
        <form
          onSubmit={async (event) => {
            event.preventDefault();
            setError(null);
            try {
              await api("/api/platform/login", {
                method: "POST",
                body: JSON.stringify({ password }),
              });
              setAuthed(true);
            } catch (caught) {
              setError(errorText(caught));
            }
          }}
        >
          <input
            type="password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            placeholder="Operator password"
            autoFocus
          />
          {error && <p className="bad">{error}</p>}
          <button className="primary" type="submit">
            Sign in
          </button>
        </form>
      </main>
    );
  }

  return <Console onSignedOut={() => setAuthed(false)} />;
}

/** A code's state, as one word plus the class that greys out a dead row. */
function inviteState(invite: Invite): { label: string; className: string } {
  if (invite.used_at) return { label: "used", className: "spent" };
  if (invite.expires_at && invite.expires_at * 1000 < Date.now()) {
    return { label: "expired", className: "spent" };
  }
  return { label: "unused", className: "" };
}

function Console({ onSignedOut }: { onSignedOut: () => void }) {
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [invites, setInvites] = useState<Invite[]>([]);
  const [requests, setRequests] = useState<AccessRequest[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [note, setNote] = useState("");
  const [expiresInDays, setExpiresInDays] = useState("14");
  const [fresh, setFresh] = useState<{ code: string; url: string } | null>(null);
  const [reissued, setReissued] = useState<{
    slug: string;
    password: string;
    admin: string;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const [nextTenants, nextInvites, nextRequests, nextStats] = await Promise.all([
        api<Tenant[]>("/api/platform/tenants"),
        api<Invite[]>("/api/platform/invites"),
        api<AccessRequest[]>("/api/platform/requests"),
        api<Stats>("/api/platform/stats"),
      ]);
      setTenants(nextTenants);
      setInvites(nextInvites);
      setRequests(nextRequests);
      setStats(nextStats);
      setError(null);
    } catch (caught) {
      setError(errorText(caught));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
    const timer = setInterval(() => void refresh(), REFRESH_MS);
    return () => clearInterval(timer);
  }, [refresh]);

  useEffect(() => {
    if (!copied) return;
    const timer = setTimeout(() => setCopied(false), 1600);
    return () => clearTimeout(timer);
  }, [copied]);

  const act = async (run: () => Promise<unknown>) => {
    try {
      await run();
      await refresh();
    } catch (caught) {
      setError(errorText(caught));
    }
  };

  const copy = async (value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
    } catch {
      toast("Couldn't copy", "error");
    }
  };

  if (loading) return <ConsoleSkeleton />;

  return (
    <main className="platform">
      <header className="platform-head">
        <h1>ToonSpeech admin</h1>
        <button
          className="ghost"
          onClick={async () => {
            await api("/api/platform/logout", { method: "POST" });
            onSignedOut();
          }}
        >
          Sign out
        </button>
      </header>

      {error && <p className="bad">{error}</p>}

      {stats && (
        <section className="stat-row">
          <Stat label="Instances" value={`${stats.tenants} / ${stats.max_tenants}`} />
          <Stat label="Active" value={stats.active} />
          <Stat label="Suspended" value={stats.suspended} />
          <Stat label="Waiting" value={stats.pending_requests} />
          <Stat label="Messages today" value={stats.requests_today} />
          <Stat label="Characters today" value={stats.chars_today.toLocaleString()} />
        </section>
      )}

      <Requests
        requests={requests}
        onAct={act}
        onMinted={() => setFresh(null)}
        copied={copied}
        onCopy={copy}
      />

      <section className="card">
        <h2>Invites</h2>
        <div className="field-row">
          <input
            value={note}
            onChange={(event) => setNote(event.target.value)}
            placeholder="Note"
          />
          <label className="inline-field">
            Expires in
            <input
              type="number"
              min={0}
              value={expiresInDays}
              onChange={(event) => setExpiresInDays(event.target.value)}
            />
            days
          </label>
          <button
            className="primary"
            onClick={() =>
              act(async () => {
                const days = Number(expiresInDays);
                const created = await api<{ invite: Invite; claim_url: string }>(
                  "/api/platform/invites",
                  {
                    method: "POST",
                    body: JSON.stringify({
                      note,
                      // 0 or blank means "no expiry" — the API reads a missing
                      // value that way, so send nothing rather than a zero.
                      expires_in_days: days > 0 ? days : null,
                    }),
                  },
                );
                setFresh({ code: created.invite.code, url: created.claim_url });
                setNote("");
              })
            }
          >
            New invite
          </button>
        </div>

        {fresh && (
          <div className="warn-box">
            <strong>Send this link:</strong> <code>{fresh.url}</code>{" "}
            <button className="ghost" onClick={() => void copy(fresh.url)}>
              {copied ? "Copied" : "Copy"}
            </button>
          </div>
        )}

        <table className="grid">
          <thead>
            <tr>
              <th>Code</th>
              <th>Note</th>
              <th>Created</th>
              <th>Expires</th>
              <th>Status</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {invites.map((invite) => {
              const state = inviteState(invite);
              return (
                <tr key={invite.code} className={state.className}>
                  <td>
                    <code>{invite.code}</code>
                  </td>
                  <td>{invite.note}</td>
                  <td>
                    <TimeAgo ts={invite.created_at} />
                  </td>
                  <td>
                    {invite.expires_at ? (
                      <TimeAgo ts={invite.expires_at} />
                    ) : (
                      <span className="muted">never</span>
                    )}
                  </td>
                  <td>{state.label}</td>
                  <td>
                    {!invite.used_at && (
                      <button
                        className="ghost"
                        onClick={() =>
                          act(() =>
                            api(`/api/platform/invites/${encodeURIComponent(invite.code)}`, {
                              method: "DELETE",
                            }),
                          )
                        }
                      >
                        Revoke
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
            {invites.length === 0 && (
              <tr>
                <td colSpan={6} className="muted">
                  No invites yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </section>

      <Instances
        tenants={tenants}
        reissued={reissued}
        copied={copied}
        onCopy={copy}
        onDismissReissue={() => setReissued(null)}
        onAct={act}
        onReissued={setReissued}
      />

      <Dialogs />
      <Toasts />
    </main>
  );
}

function Stat({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="stat">
      <span className="stat-value">{value}</span>
      <span className="stat-label">{label}</span>
    </div>
  );
}

/* The inbox.

   This is the surface the landing page's form feeds, and it sits above the
   invite table on purpose: a code minted from a request is the common path now,
   and the manual "New invite" button below it is the exception for somebody the
   operator already knows.

   Pending first, then everything decided, because the queue is the only part of
   this an operator has to act on — a declined row from March is history. */

type RequestFilter = "new" | "all";

const REQUEST_FILTERS: { id: RequestFilter; label: string }[] = [
  { id: "new", label: "Waiting" },
  { id: "all", label: "All" },
];

function Requests({
  requests,
  onAct,
  onMinted,
  copied,
  onCopy,
}: {
  requests: AccessRequest[];
  onAct: (run: () => Promise<unknown>) => Promise<void>;
  onMinted: (next: { handle: string; url: string }) => void;
  copied: boolean;
  onCopy: (value: string) => void;
}) {
  const [filter, setFilter] = useState<RequestFilter>("new");
  const [minted, setMinted] = useState<{ handle: string; url: string } | null>(null);

  const waiting = requests.filter((request) => request.status === "new");
  const shown = filter === "new" ? waiting : requests;

  return (
    <section className="card">
      <h2>
        Requests
        {waiting.length > 0 && <span className="count-badge">{waiting.length}</span>}
      </h2>

      {minted && (
        <div className="warn-box">
          <strong>Code for {minted.handle}:</strong> <code>{minted.url}</code>{" "}
          <button className="ghost" onClick={() => onCopy(minted.url)}>
            {copied ? "Copied" : "Copy"}
          </button>
          <button className="ghost" onClick={() => setMinted(null)}>
            Done
          </button>
        </div>
      )}

      <div className="table-controls">
        <div className="tabs compact" role="tablist" aria-label="Filter requests">
          {REQUEST_FILTERS.map((candidate) => (
            <button
              key={candidate.id}
              role="tab"
              aria-selected={filter === candidate.id}
              onClick={() => setFilter(candidate.id)}
            >
              {candidate.label}
            </button>
          ))}
        </div>
        <span className="muted">
          {waiting.length === 0
            ? "Nothing waiting"
            : `${waiting.length} waiting of ${requests.length}`}
        </span>
      </div>

      <table className="grid">
        <thead>
          <tr>
            <th>Who</th>
            <th>Contact</th>
            <th>Note</th>
            <th>Asked</th>
            <th>Status</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {shown.map((request) => (
            <tr key={request.id} className={request.status === "new" ? "" : "spent"}>
              <td>{request.handle}</td>
              <td>
                <code>{request.contact}</code>
              </td>
              {/* Somebody else's words in the operator's console: rendered as
                  text by React, never as markup, and clipped so a wall of it
                  cannot push the buttons off the row. */}
              <td className="note-cell" title={request.note}>
                {request.note || <span className="muted">—</span>}
              </td>
              <td>
                <TimeAgo ts={request.created_at} />
              </td>
              <td>
                {request.status === "invited" && request.invite_code ? (
                  <span title={`code ${request.invite_code}`}>invited</span>
                ) : (
                  request.status
                )}
              </td>
              <td className="actions">
                {request.status === "new" && (
                  <>
                    <button
                      className="primary"
                      onClick={() =>
                        void onAct(async () => {
                          const created = await api<{ claim_url: string }>(
                            `/api/platform/requests/${request.id}/invite`,
                            { method: "POST", body: JSON.stringify({ expires_in_days: 14 }) },
                          );
                          const next = { handle: request.handle, url: created.claim_url };
                          setMinted(next);
                          onMinted(next);
                        })
                      }
                    >
                      Send a code
                    </button>
                    <button
                      className="ghost"
                      onClick={() =>
                        void onAct(() =>
                          api(`/api/platform/requests/${request.id}/decline`, {
                            method: "POST",
                          }),
                        )
                      }
                    >
                      Decline
                    </button>
                  </>
                )}
                <button
                  className="danger"
                  onClick={async () => {
                    // Declining keeps the row so the same person is recognisable
                    // next time; deleting is for the ones that are not people.
                    const confirmed = await confirmDialog({
                      title: `Delete this request?`,
                      body: "This can't be undone.",
                      confirmLabel: "Delete",
                      danger: true,
                    });
                    if (!confirmed) return;
                    void onAct(() =>
                      api(`/api/platform/requests/${request.id}`, { method: "DELETE" }),
                    );
                  }}
                >
                  Delete
                </button>
              </td>
            </tr>
          ))}
          {shown.length === 0 && (
            <tr>
              <td colSpan={6} className="muted">
                {requests.length === 0
                  ? "No requests yet."
                  : "Nothing waiting."}
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </section>
  );
}

/* The instances table.

   It was a plain list, which is right at ten tenants and useless at a hundred:
   an operator arrives here with a question — "who is this person who emailed
   me", "who has gone quiet", "who is about to hit the cap" — and a fixed order
   answers none of them. Sorting and a filter answer all three, and the row
   opens rather than the table growing a seventh column to carry the rest. */

type SortKey = "slug" | "today" | "total" | "seen";
type StatusFilter = "all" | "active" | "suspended";

const STATUS_FILTERS: { id: StatusFilter; label: string }[] = [
  { id: "all", label: "All" },
  { id: "active", label: "Active" },
  { id: "suspended", label: "Suspended" },
];

interface InstancesProps {
  tenants: Tenant[];
  reissued: { slug: string; password: string; admin: string } | null;
  copied: boolean;
  onCopy: (value: string) => void;
  onDismissReissue: () => void;
  onAct: (run: () => Promise<unknown>) => Promise<void>;
  onReissued: (next: { slug: string; password: string; admin: string }) => void;
}

function Instances({
  tenants,
  reissued,
  copied,
  onCopy,
  onDismissReissue,
  onAct,
  onReissued,
}: InstancesProps) {
  const [needle, setNeedle] = useState("");
  const [status, setStatus] = useState<StatusFilter>("all");
  const [sort, setSort] = useState<{ key: SortKey; desc: boolean }>({
    key: "seen",
    desc: true,
  });
  const [expanded, setExpanded] = useState<number | null>(null);

  const shown = useMemo(() => {
    const query = needle.toLowerCase().trim();
    const filtered = tenants.filter((tenant) => {
      if (status !== "all" && tenant.status !== status) return false;
      if (!query) return true;
      return (
        tenant.slug.toLowerCase().includes(query) ||
        tenant.display_name.toLowerCase().includes(query)
      );
    });

    const value = (tenant: Tenant): number | string => {
      switch (sort.key) {
        case "slug":
          return tenant.slug;
        case "today":
          return tenant.requests_today;
        case "total":
          return tenant.requests_total;
        case "seen":
          // Never-seen sorts as oldest either way, rather than as "just now".
          return tenant.last_seen_at ?? 0;
      }
    };

    return [...filtered].sort((left, right) => {
      const a = value(left);
      const b = value(right);
      const order = typeof a === "string" ? a.localeCompare(b as string) : a - (b as number);
      return sort.desc ? -order : order;
    });
  }, [tenants, needle, status, sort]);

  function toggleSort(key: SortKey) {
    setSort((was) =>
      // A second click on the same column reverses it; a new column starts
      // descending, which is what "most" means for every one of these.
      was.key === key ? { key, desc: !was.desc } : { key, desc: true },
    );
  }

  const heading = (key: SortKey, label: string) => (
    <th>
      <button
        className="sort"
        aria-sort={sort.key === key ? (sort.desc ? "descending" : "ascending") : "none"}
        onClick={() => toggleSort(key)}
      >
        {label}
        <span className="sort-caret" aria-hidden="true">
          {sort.key === key ? (sort.desc ? "▾" : "▴") : ""}
        </span>
      </button>
    </th>
  );

  return (
    <section className="card">
      <h2>Instances</h2>

      {reissued && (
        <div className="warn-box">
          <strong>New passwords for {reissued.slug}</strong>. These are only
          shown once.
          <div>
            Password: <code>{reissued.password}</code>
          </div>
          <div>
            Admin: <code>{reissued.admin}</code>
          </div>
          {/* One button, because the operator is about to paste these into a DM
              and there is no screen that can show them again. */}
          <div className="warn-actions">
            <button
              onClick={() =>
                onCopy(
                  [
                    `${reissued.slug}`,
                    `Password:        ${reissued.password}`,
                    `Admin password:  ${reissued.admin}`,
                  ].join("\n"),
                )
              }
            >
              {copied ? "Copied both" : "Copy both"}
            </button>
            <button className="ghost" onClick={onDismissReissue}>
              Done
            </button>
          </div>
        </div>
      )}

      <div className="table-controls">
        <input
          type="search"
          aria-label="Filter instances"
          placeholder="Filter by subdomain or name…"
          value={needle}
          onChange={(event) => setNeedle(event.target.value)}
        />
        <div className="tabs compact" role="tablist" aria-label="Filter by status">
          {STATUS_FILTERS.map((candidate) => (
            <button
              key={candidate.id}
              role="tab"
              aria-selected={status === candidate.id}
              onClick={() => setStatus(candidate.id)}
            >
              {candidate.label}
            </button>
          ))}
        </div>
        <span className="muted">
          {shown.length === tenants.length
            ? `${tenants.length} total`
            : `${shown.length} of ${tenants.length}`}
        </span>
      </div>

      <table className="grid">
        <thead>
          <tr>
            <th aria-label="Expand" />
            {heading("slug", "Subdomain")}
            <th>Name</th>
            <th>14 days</th>
            {heading("today", "Today")}
            {heading("total", "Total")}
            {heading("seen", "Last seen")}
            <th>Status</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {shown.map((tenant) => (
            <InstanceRow
              key={tenant.id}
              tenant={tenant}
              open={expanded === tenant.id}
              onToggle={() =>
                setExpanded((was) => (was === tenant.id ? null : tenant.id))
              }
              onAct={onAct}
              onReissued={onReissued}
            />
          ))}
          {shown.length === 0 && (
            <tr>
              <td colSpan={9} className="muted">
                {tenants.length === 0
                  ? "No instances yet. Mint an invite above."
                  : "Nothing matches that filter."}
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </section>
  );
}

function InstanceRow({
  tenant,
  open,
  onToggle,
  onAct,
  onReissued,
}: {
  tenant: Tenant;
  open: boolean;
  onToggle: () => void;
  onAct: (run: () => Promise<unknown>) => Promise<void>;
  onReissued: (next: { slug: string; password: string; admin: string }) => void;
}) {
  const daily = tenant.daily ?? [];
  const busiest = Math.max(0, ...daily);

  return (
    <>
      <tr className={tenant.status}>
        <td>
          <button
            className="disclosure"
            aria-expanded={open}
            aria-label={`Details for ${tenant.slug}`}
            onClick={onToggle}
          >
            {open ? "▾" : "▸"}
          </button>
        </td>
        <td>
          <code>{tenant.slug}</code>
        </td>
        <td>{tenant.display_name}</td>
        <td>
          <Sparkline values={daily} label={tenant.slug} />
        </td>
        <td>
          {tenant.requests_today} msg · {tenant.chars_today.toLocaleString()} ch
        </td>
        <td>{tenant.requests_total}</td>
        <td>{tenant.last_seen_at ? <TimeAgo ts={tenant.last_seen_at} /> : "never"}</td>
        <td>{tenant.status}</td>
        <td className="actions">
          <button
            className="ghost"
            onClick={async () => {
              const suspending = tenant.status === "active";
              if (suspending) {
                // Takes the instance off the air mid-stream, so it asks —
                // resuming is harmless and does not.
                const confirmed = await confirmDialog({
                  title: `Suspend ${tenant.slug}?`,
                  body: "The panel and overlay go offline until resumed. Nothing is deleted.",
                  confirmLabel: "Suspend",
                });
                if (!confirmed) return;
              }
              void onAct(() =>
                api(`/api/platform/tenants/${tenant.id}`, {
                  method: "PATCH",
                  body: JSON.stringify({
                    status: suspending ? "suspended" : "active",
                  }),
                }),
              );
            }}
          >
            {tenant.status === "active" ? "Suspend" : "Resume"}
          </button>
          <button
            className="ghost"
            onClick={async () => {
              const confirmed = await confirmDialog({
                title: `Reissue passwords for ${tenant.slug}?`,
                body: "Both passwords change and the old ones stop working.",
                confirmLabel: "Reissue",
              });
              if (!confirmed) return;
              void onAct(async () => {
                const next = await api<{
                  password: string;
                  admin_password: string;
                }>(`/api/platform/tenants/${tenant.id}/rotate`, { method: "POST" });
                onReissued({
                  slug: tenant.slug,
                  password: next.password,
                  admin: next.admin_password,
                });
              });
            }}
          >
            New passwords
          </button>
          <button
            className="danger"
            onClick={async () => {
              // Irreversible and takes the audio with it, so the subdomain has
              // to be typed rather than clicked past.
              const confirmed = await confirmDialog({
                title: `Delete ${tenant.slug}?`,
                body: "Deletes the instance, its history and audio. This can't be undone.",
                confirmLabel: "Delete instance",
                danger: true,
                typeToConfirm: tenant.slug,
              });
              if (!confirmed) return;
              void onAct(() =>
                api(`/api/platform/tenants/${tenant.id}`, { method: "DELETE" }),
              );
            }}
          >
            Delete
          </button>
        </td>
      </tr>

      {/* The detail that would otherwise be three more columns nobody scans. */}
      {open && (
        <tr className="detail-row">
          <td colSpan={9}>
            <div className="detail">
              <div>
                <span className="field-label">Created</span>
                <TimeAgo ts={tenant.created_at} />
              </div>
              <div>
                <span className="field-label">Characters today</span>
                {tenant.chars_today.toLocaleString()}
              </div>
              <div>
                <span className="field-label">Busiest day (14d)</span>
                {busiest} message{busiest === 1 ? "" : "s"}
              </div>
              <div>
                <span className="field-label">Sent in 14d</span>
                {daily.reduce((sum, value) => sum + value, 0)}
              </div>
              <div>
                <span className="field-label">Panel</span>
                {/* No token here and nothing to sign in with: the operator can
                    see that an instance exists, not get into it. */}
                <code>{tenant.slug}</code>
              </div>
              <div>
                <span className="field-label">Internal id</span>
                <code>{tenant.id}</code>
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}
